import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";

import type { 
  GetSmsTemplatesResponse, 
  GetSmsSettingsResponse,
  SmsSettings,
  UpdateSmsSettingsResponse 
} from "../types/sms-settings";

export const getSmsTemplates = async (): Promise<GetSmsTemplatesResponse> => {
  const url = getApiUrl(endpoint.smsSettings.getSmsTemplates);

  try {
    const response = await handleApiCall(() => api.get({
      url,
      auth: true,
    })) as GetSmsTemplatesResponse;

    return response;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message || "Failed to fetch SMS templates", "error");
    throw error;
  }
};

export const getSmsSettings = async (): Promise<GetSmsSettingsResponse> => {
  const url = getApiUrl(endpoint.smsSettings.getSmsSettings);

  try {
    const response = await handleApiCall(() => api.get({
      url,
      auth: true,
    })) as GetSmsSettingsResponse;

    return response;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message || "Failed to fetch SMS settings", "error");
    throw error;
  }
};

export const updateSmsSettings = async (
  data: SmsSettings
): Promise<UpdateSmsSettingsResponse> => {
  const url = getApiUrl(endpoint.smsSettings.updateSmsSettings);

  try {
    const response = await handleApiCall(() => api.put({
      url,
      body: data,
      auth: true,
    })) as { data: UpdateSmsSettingsResponse };

    showToast("SMS settings updated successfully", "success");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createSmsTemplate = async (data: {
  name: string;
  content: string;
  category: string;
  scope?: 'personal' | 'agency';
}): Promise<any> => {
  const url = getApiUrl(endpoint.smsSettings.getSmsTemplates);

  try {
    const response = await handleApiCall(() => api.post({
      url,
      body: data,
      auth: true,
    })) as any;

    showToast("SMS template created successfully", "success");
    return response;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message || "Failed to create SMS template", "error");
    throw error;
  }
};

export const updateSmsTemplate = async (
  id: string,
  data: { name?: string; content?: string; category?: string; scope?: 'personal' | 'agency' }
): Promise<any> => {
  const url = `${getApiUrl(endpoint.smsSettings.getSmsTemplates)}/${id}`;

  try {
    const response = await handleApiCall(() => api.put({
      url,
      body: data,
      auth: true,
    })) as any;

    showToast("SMS template updated successfully", "success");
    return response;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message || "Failed to update SMS template", "error");
    throw error;
  }
};

export const deleteSmsTemplate = async (id: string): Promise<any> => {
  const url = `${getApiUrl(endpoint.smsSettings.getSmsTemplates)}/${id}`;

  try {
    const response = await handleApiCall(() => api.delete({
      url,
      auth: true,
    })) as any;

    showToast("SMS template deleted successfully", "success");
    return response;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message || "Failed to delete SMS template", "error");
    throw error;
  }
};